import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useBehaviorSubject } from '@euler/utils/hooks/useBehaviorSubject';
import { useCreation } from '@euler/utils/hooks/useCreation';
import {
  ReactNode,
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { TextInput, TextInputProps, View } from 'react-native';
import { BehaviorSubject } from 'rxjs';
import { HoldZone } from '../functions/holdZone';
import { useHoldToTalk } from '../functions/useHoldToTalk';
import { useSpokenIntoBox } from '../functions/useSpokenIntoBox';
import { SpokenVoice, VoiceTurn } from '../functions/useVoiceTurn';
import { MicButton } from './MicButton';
import { TalkBar, kTalkBarLabel } from './TalkBar';
import { TextBox } from './TextBox';
import { VoiceHoldLayers } from './VoiceHoldLayers';

export interface VoiceComposerHandle {
  /** Opens the box (from the bar too) and puts the keyboard up. */
  focus: () => void;
  /** The box's input while it shows, for a host that moves its caret. */
  input: () => TextInput | null;
}

export interface VoiceComposerProps {
  /** The capture's turn: a dictation one, or a host's own (coaching). */
  voice: VoiceTurn;
  value: string;
  onChangeText: (text: string) => void;
  /**
   * Words to go, from the box's send key or the editor, with their voice
   * when they were spoken; the box's words are the host's to clear. A hold
   * let go on 发送 goes by the turn's own line instead.
   */
  onSend: (
    text: string,
    voice: SpokenVoice | null,
    from: 'box' | 'editor',
  ) => void;
  /** Words from the box or the editor may go now. */
  canType?: boolean;
  /** Talking is open. */
  canTalk?: boolean;
  /** Left of the bar or box; nothing by default. */
  leading?: ReactNode;
  /** What the bar says, or a view of its own. */
  barLabel?: string | ReactNode;
  placeholder?: string;
  /**
   * One line, sent by the keyboard's send key; otherwise the box grows and
   * the host's own button sends.
   */
  sendOnReturn?: boolean;
  /**
   * Words are being dictated into the box: a host's own send button waits,
   * or the words still coming would land in a box it just cleared.
   */
  onDictatingChange?: (dictating: boolean) => void;
  inputProps?: Omit<
    TextInputProps,
    'value' | 'onChangeText' | 'placeholder' | 'multiline'
  >;
}

/**
 * The app's composer on a phone, as in WeChat: a bar to hold and talk (the
 * overlay says where letting go takes the words: 发送, 取消 or 编辑) or tap
 * to type; typing, the box's mic dictates into it on a tap and talks as the
 * bar does when held. The host owns the text, what sits left of it, and
 * any button right of it.
 */
export const VoiceComposer = memo(
  forwardRef<VoiceComposerHandle, VoiceComposerProps>(function VoiceComposer(
    {
      voice,
      value,
      onChangeText,
      onSend,
      canType = true,
      canTalk = true,
      leading,
      barLabel = kTalkBarLabel,
      placeholder = '输入消息…',
      sendOnReturn = false,
      inputProps,
      onDictatingChange,
    },
    ref,
  ) {
    const { rpx } = useResponsiveContext();
    const input = useRef<TextInput>(null);
    // Asked to focus from the bar: once the box shows.
    const focusing = useRef(false);
    const zone$ = useCreation(() => new BehaviorSubject<HoldZone>('send'), []);
    const [typing, setTyping] = useState(false);
    useImperativeHandle(ref, () => ({
      focus: () => {
        if (input.current) input.current.focus();
        else {
          focusing.current = true;
          setTyping(true);
        }
      },
      input: () => input.current,
    }));
    useEffect(() => {
      if (!typing || !focusing.current) return;
      focusing.current = false;
      input.current?.focus();
    }, [typing]);
    const [state] = useBehaviorSubject(voice.state$);
    const dictating = state.kind === 'holding' && state.via === 'dictate';
    const inserting =
      dictating || (state.kind === 'recognizing' && state.outcome === 'insert');
    const dictatingChanged = useRef(onDictatingChange);
    dictatingChanged.current = onDictatingChange;
    useEffect(() => {
      dictatingChanged.current?.(inserting);
    }, [inserting]);
    const spoken = useSpokenIntoBox({
      voice,
      value,
      onChangeText,
      canTalk,
      onOpen: () => setTyping(true),
    });
    const send = () => {
      if (!canType || !value.trim() || inserting) return;
      onSend(value, spoken.spokenVoice.current, 'box');
      spoken.spokenVoice.current = null;
    };
    const bar = useHoldToTalk({
      voice,
      zone$,
      canTalk,
      onTap: () => setTyping(true),
    });
    const mic = useHoldToTalk({
      voice,
      zone$,
      canTalk: canTalk && !inserting,
      onTap: spoken.toggleDictation,
    });
    return (
      <View
        style={{
          flex: 1,
          flexDirection: 'row',
          alignItems: 'flex-end',
          gap: rpx(8),
        }}
      >
        {leading}
        {typing || !!value ? (
          <TextBox
            inputRef={input}
            value={value}
            onChangeText={text => {
              onChangeText(text);
              if (!text.trim()) spoken.spokenVoice.current = null;
            }}
            placeholder={placeholder}
            onSubmit={sendOnReturn ? send : null}
            autoFocus={!value}
            // Talking blurs the box (the keyboard goes); it stays for the
            // words, and under a finger holding its mic.
            onBlur={() => {
              if (!value.trim() && voice.state$.getValue().kind === 'idle')
                setTyping(false);
            }}
            inputProps={inputProps}
            mic={
              <MicButton
                dictating={dictating}
                canTalk={canTalk}
                level$={voice.level$}
                panHandlers={mic.panHandlers}
              />
            }
          />
        ) : (
          <TalkBar
            label={barLabel}
            canTalk={canTalk}
            panHandlers={bar.panHandlers}
          />
        )}
        <VoiceHoldLayers
          voice={voice}
          zone$={zone$}
          editing={spoken.editing}
          canSend={canType}
          onSendEdited={(line, spokenVoice) =>
            onSend(line, spokenVoice, 'editor')
          }
          onCloseEditor={spoken.closeEditor}
        />
      </View>
    );
  }),
);
